<template>
  <div class="game-login-container">
    <div class="game-login-card">
      <div class="game-login-header">
        <h1>
          <sup>Welcome to</sup>
          OnCall<span>PRIORITY</span>
        </h1>
      </div>

      <!-- Tab switcher -->
      <div class="login-tabs">
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'code' }"
          @click="switchTab('code')"
        >Game Code</button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'email' }"
          @click="switchTab('email')"
        >Sign In</button>
 
      </div>

      <!-- ---- EMAIL / PASSWORD LOGIN ---- -->
      <div v-if="activeTab === 'email'" class="game-login-form">
        <div class="input-group">
          <label for="loginEmail">Email</label>
          <input
            id="loginEmail"
            v-model="emailInput"
            type="email"
            placeholder="your@email.com"
            autocomplete="email"
            :class="{ error: errorMessage }"
            @keyup.enter="loginWithEmail"
          />
        </div>
        <div class="input-group">
          <label for="loginPassword">Password</label>
          <input
            id="loginPassword"
            v-model="passwordInput"
            type="password"
            placeholder="Password"
            autocomplete="current-password"
            :class="{ error: errorMessage }"
            @keyup.enter="loginWithEmail"
          />
        </div>

        <button
          class="login-btn"
          :disabled="!emailInput.trim() || !passwordInput || loading"
          @click="loginWithEmail"
        >
          <span v-if="loading">Signing in…</span>
          <span v-else>Sign In</span>
        </button>

        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

        <div class="game-login-footer">
          <p>Contact your administrator if you have not received your login details.</p>
        </div>
      </div>

      <!-- ---- GAME CODE LOGIN ---- -->
      <div v-else class="game-login-form">
        <div class="input-group">
          <label for="gameCode">Game Code</label>
          <input
            id="gameCode"
            v-model="gameCode"
            type="text"
            placeholder="Enter game code"
            autocomplete="off"
            :class="{ error: errorMessage }"
            @keyup.enter="validateGameCode"
          />
        </div>

        <button
          class="login-btn"
          :disabled="!gameCode.trim() || loading"
          @click="validateGameCode"
        >
          <span v-if="loading">Checking…</span>
          <span v-else>Continue</span>
        </button>

        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

        <div class="game-login-footer">
          <p>Contact your facilitator for the game code.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { supabase } from '@/supabase';
import { useGameStore } from '@/stores/gameStore';

const router = useRouter();
const gameStore = useGameStore();

// --- shared state ---
const activeTab = ref('code');
const loading = ref(false);
const errorMessage = ref('');

// --- email/password state ---
const emailInput = ref('');
const passwordInput = ref('');

// --- game-code state ---
const gameCode = ref('');

// Valid facilitator game codes (UX gate only, not security)
const validGameCodes = [
  'ONCALL2024',
  'PRIORITY',
  'GAME123',
  'TESTCODE'
];

const switchTab = (tab) => {
  activeTab.value = tab;
  errorMessage.value = '';
};

// ---- Email / Password login via Supabase Auth ----
const loginWithEmail = async () => {
  if (!emailInput.value.trim() || !passwordInput.value) return;

  loading.value = true;
  errorMessage.value = '';

  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: emailInput.value.trim(),
      password: passwordInput.value,
    });

    if (error) {
      console.error('signInWithPassword error:', error);
      errorMessage.value = error.message || 'Invalid email or password. Please try again.';
      return;
    }

    // Fetch the user's department access
    const { data: accessRows, error: accessErr } = await supabase
      .from('user_department_access')
      .select('department_id, access_mode')
      .eq('user_id', data.user.id);

    if (accessErr) {
      console.error('Failed to load department access:', accessErr);
      errorMessage.value = 'Login succeeded but could not load your access. Please try again.';
      await supabase.auth.signOut();
      return;
    }

    if (!accessRows || accessRows.length === 0) {
      errorMessage.value = 'Your account has no department access assigned. Contact your administrator.';
      await supabase.auth.signOut();
      return;
    }

    // Store access in localStorage for the router guard + game store
    localStorage.setItem('userDeptAccess', JSON.stringify(accessRows));

    // If exactly one department is assigned, go straight to game; else go to departments picker
    if (accessRows.length === 1) {
      const { department_id, access_mode } = accessRows[0];

      const sessionPayload = {
        userId: data.user.id,
        authType: 'email',
        isDemo: access_mode === 'demo_only',
        demoDepartmentId: access_mode === 'demo_only' ? department_id : null,
        timestamp: Date.now(),
        expiresAt: Date.now() + 24 * 60 * 60 * 1000,
      };
      localStorage.setItem('gameSession', JSON.stringify(sessionPayload));
      gameStore.applyGameSessionFromStorage();
      await gameStore.resetGame();
      router.push({ name: 'Game', params: { departmentId: String(department_id) } });
    } else {
      const sessionPayload = {
        userId: data.user.id,
        authType: 'email',
        isDemo: false,
        timestamp: Date.now(),
        expiresAt: Date.now() + 24 * 60 * 60 * 1000,
      };
      localStorage.setItem('gameSession', JSON.stringify(sessionPayload));
      gameStore.applyGameSessionFromStorage();
      router.push({ name: 'CardDepartments' });
    }
  } catch (err) {
    console.error('Login error:', err);
    errorMessage.value = 'An unexpected error occurred. Please try again.';
  } finally {
    loading.value = false;
  }
};

// ---- Game code login (existing behaviour preserved) ----
const createGameSession = ({ isDemo = false, demoDepartmentId = null } = {}) => {
  const sessionId = 'player_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  const normalized = gameCode.value.trim().toUpperCase();

  const payload = {
    userId: sessionId,
    authType: 'code',
    gameCode: normalized,
    isDemo,
    timestamp: Date.now(),
    expiresAt: Date.now() + 24 * 60 * 60 * 1000,
  };

  if (isDemo && demoDepartmentId != null) {
    payload.demoDepartmentId = Number(demoDepartmentId);
  }

  localStorage.setItem('gameSession', JSON.stringify(payload));
};

const validateGameCode = async () => {
  if (!gameCode.value.trim()) {
    errorMessage.value = 'Please enter a game code';
    return;
  }

  loading.value = true;
  errorMessage.value = '';

  try {
    const normalized = gameCode.value.trim().toUpperCase();

    // Check if this is a department demo code
    const { data: demoDept, error: demoErr } = await supabase
      .from('departments')
      .select('id')
      .eq('demo_access_code', normalized)
      .maybeSingle();

    if (demoErr) {
      console.error('Demo code lookup error:', demoErr);
      errorMessage.value = 'Could not verify code. Please try again.';
      return;
    }

    if (demoDept?.id != null) {
      localStorage.setItem('gameCode', normalized);
      createGameSession({ isDemo: true, demoDepartmentId: demoDept.id });
      await gameStore.resetGame();
      gameStore.applyGameSessionFromStorage();
      router.push({ name: 'Game', params: { departmentId: String(demoDept.id) } });
      return;
    }

    await new Promise(resolve => setTimeout(resolve, 400));

    if (validGameCodes.includes(normalized)) {
      localStorage.setItem('gameCode', normalized);
      createGameSession({ isDemo: false });
      await gameStore.resetGame();
      gameStore.applyGameSessionFromStorage();
      router.push('/');
      return;
    }

    errorMessage.value = 'Invalid game code. Please try again.';
  } catch (error) {
    console.error('Authentication error:', error);
    errorMessage.value = 'Authentication failed. Please try again.';
  } finally {
    loading.value = false;
  }
};
</script>

<style src="../styles/_gameLogin.scss" lang="scss" scoped></style>
